import type { TimeOffRequest } from "../lib/api";
import { TIME_OFF_TYPES } from "../lib/shared/timeOffTypes";
import { formatDateRange, formatTimestamp, acknowledgedByLabel } from "../lib/format";
import StatusBadge from "./StatusBadge";

interface Props {
  requests: TimeOffRequest[];
  showPerson?: boolean;
  /** When set, adds an "Approver" column showing this name on every row -- who to reach out to if a request isn't decided in a timely manner. Opt-in since it's only meaningful on "my requests" style views (a single person's own manager), not the Acknowledgements/Executive tables. */
  approverName?: string | null;
  /** Adds a "Submitted" column showing when each request was originally made. Opt-in since it's mainly useful on the Org-wide "All requests" table, not every list. */
  showSubmittedAt?: boolean;
  /** Adds a "Decided" column showing when each request was decided. Opt-in since it's only meaningful for already-decided requests (Past decisions), not a pending queue where decided_at is still null. */
  showDecidedAt?: boolean;
  /** Adds an "Acknowledged by" column -- the Org-wide "All requests" table only, since decided_by_name/requester_manager_name are only populated on that scope. */
  showAcknowledgedBy?: boolean;
  emptyMessage: string;
  renderActions?: (request: TimeOffRequest) => React.ReactNode;
}

export default function RequestTable({
  requests,
  showPerson,
  approverName,
  showSubmittedAt,
  showDecidedAt,
  showAcknowledgedBy,
  emptyMessage,
  renderActions,
}: Props) {
  if (requests.length === 0) {
    return <p className="di-small empty-state">{emptyMessage}</p>;
  }

  return (
    <div className="table-scroll">
      <table className="di-table">
        <thead>
          <tr>
            {showPerson && <th>Person</th>}
            <th>Type</th>
            <th>Dates</th>
            <th>Status</th>
            {approverName !== undefined && <th>Manager</th>}
            {showSubmittedAt && <th>Submitted</th>}
            {showDecidedAt && <th>Decided</th>}
            {showAcknowledgedBy && <th>Acknowledged by</th>}
            <th>Note</th>
            {renderActions && <th aria-label="Actions" />}
          </tr>
        </thead>
        <tbody>
          {requests.map((r) => (
            <tr key={r.id}>
              {showPerson && <td>{r.person_name ?? r.person_email}</td>}
              <td>{TIME_OFF_TYPES[r.type].label}</td>
              <td>{formatDateRange(r.start_date, r.end_date, r.day_part)}</td>
              <td>
                <StatusBadge status={r.status} />
                {r.status === "denied" && r.deny_reason && (
                  <span className="di-small deny-reason"> — {r.deny_reason}</span>
                )}
              </td>
              {approverName !== undefined && <td className="di-small">{approverName ?? "—"}</td>}
              {showSubmittedAt && <td className="di-small">{formatTimestamp(r.submitted_at)}</td>}
              {showDecidedAt && <td className="di-small">{formatTimestamp(r.decided_at)}</td>}
              {showAcknowledgedBy && <td className="di-small">{acknowledgedByLabel(r)}</td>}
              <td className="di-small">{r.note ?? "—"}</td>
              {renderActions && <td className="actions-cell">{renderActions(r)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
